import React from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ui } from '../../config/uiSizes';
import { useAuth } from '../../hooks/auth';
import {
    getRollbackPlan,
    approveRollback
} from '../../services/Rollback/rollbackMigrationService';
import type { Migration } from '../../types/migration';
import type { RollbackPlanResponse, RollbackResource } from '../../types/rollback';
import {
    toFriendlyName,
    formatValue,
    formatPropertyName,
    formatUtcTimestampLocal
} from '../../utils/formatters';
import { getTypeLabelByApiName } from '../../utils/resourceTypes';
import Spinner from '../../components/migratorDashboard/Spinner';
import RollbackConfirmModal from '../../components/Modals/Rollback/RollbackConfirmModal';
import { logger } from '../../utils/logger';

// ── Resource grouping ─────────────────────────────────────────────────────────

interface ResourceTypeGroup {
    resourceType: string;
    label: string;
    deletes: RollbackResource[];
    reverts: RollbackResource[];
    restores: RollbackResource[];
}

function groupByType(resources: RollbackResource[]): ResourceTypeGroup[] {
    const map = new Map<string, ResourceTypeGroup>();
    for (const r of resources) {
        if (!map.has(r.resourceType)) {
            map.set(r.resourceType, {
                resourceType: r.resourceType,
                label: getTypeLabelByApiName(toFriendlyName(r.resourceType)),
                deletes: [],
                reverts: [],
                restores: []
            });
        }
        const g = map.get(r.resourceType)!;
        if (r.action === 'delete') g.deletes.push(r);
        else if (r.action === 'revert') g.reverts.push(r);
        else g.restores.push(r);
    }
    return Array.from(map.values());
}

const ROLLBACK_WINDOW_DAYS = 5;

function computeDaysSince(isoUtc: string | undefined): number | null {
    if (!isoUtc) return null;
    return Math.floor((Date.now() - new Date(isoUtc).getTime()) / (1000 * 60 * 60 * 24));
}

interface BannerConfig {
    variant: 'green' | 'amber' | 'red';
    message: string;
}

function buildBannerConfig(days: number): BannerConfig {
    const daysRemaining = Math.max(0, ROLLBACK_WINDOW_DAYS - days);
    const dayStr = (n: number) => `${n} day${n !== 1 ? 's' : ''}`;

    if (days <= 2) {
        return {
            variant: 'green',
            message: `${dayStr(days)} since migration completion — environment changes are unlikely. Automated rollback available for ${dayStr(daysRemaining)} more.`,
        };
    }
    if (days <= 4) {
        return {
            variant: 'amber',
            message: `${dayStr(days)} since migration completion — your environment may have changed since this migration ran. Automated rollback available for ${dayStr(daysRemaining)} more.`,
        };
    }
    if (days === ROLLBACK_WINDOW_DAYS) {
        return {
            variant: 'red',
            message: '5 days since migration completion — significant time has passed. Manual review is strongly recommended before proceeding. Automated rollback will no longer be available after today.',
        };
    }
    return {
        variant: 'red',
        message: `${dayStr(days)} since migration completion — automated rollback is no longer available.`,
    };
}

// ── Chevron SVG ───────────────────────────────────────────────────────────────

const ChevronRight: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        className={className}
        fill="currentColor"
        viewBox="0 0 20 20"
    >
        <path
            fillRule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clipRule="evenodd"
        />
    </svg>
);

// ── Constants ─────────────────────────────────────────────────────────────────

type PollStatus = 'idle' | 'loading' | 'ready' | 'error';

// ── Page ──────────────────────────────────────────────────────────────────────

const RollbackPreviewPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { accessToken } = useAuth();

    const migrationId = searchParams.get('migrationId') ?? '';
    const jobFromState = (location.state as { job?: Migration } | null)?.job ?? null;

    // ── State ──────────────────────────────────────────────────────────────────

    const [pollStatus, setPollStatus] = React.useState<PollStatus>('idle');
    const migration = jobFromState;
    const [plan, setPlan] = React.useState<RollbackPlanResponse | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [approveLoading, setApproveLoading] = React.useState(false);

    // ── Mount: fetch the rollback plan directly (plan is already ready) ──────

    React.useEffect(() => {
        if (!migrationId || !accessToken) return;

        let cancelled = false;

        const load = async () => {
            try {
                setPollStatus('loading');
                const planData = await getRollbackPlan(accessToken, migrationId);
                if (cancelled) return;
                setPlan(planData);
                setPollStatus('ready');
            } catch (e) {
                if (cancelled) return;
                const msg = e instanceof Error ? e.message : 'Failed to load rollback plan.';
                setErrorMessage(msg);
                setPollStatus('error');
                logger.error('Error fetching rollback plan', { migrationId, error: msg });
            }
        };

        load();

        return () => { cancelled = true; };
    }, [migrationId, accessToken]);

    // ── Retry ──────────────────────────────────────────────────────────────────

    const handleRetry = React.useCallback(async () => {
        if (!accessToken) return;
        try {
            setPollStatus('loading');
            setErrorMessage(null);
            setPlan(null);
            const planData = await getRollbackPlan(accessToken, migrationId);
            setPlan(planData);
            setPollStatus('ready');
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Failed to load rollback plan.');
            setPollStatus('error');
        }
    }, [accessToken, migrationId]);

    // ── Approve ────────────────────────────────────────────────────────────────

    const handleConfirmRollback = React.useCallback(async () => {
        if (!accessToken) return;
        try {
            setApproveLoading(true);
            await approveRollback(accessToken, migrationId);

            navigate('/dashboard');
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to approve rollback.';
            setErrorMessage(msg);
            setApproveLoading(false);
            setConfirmOpen(false);
            logger.error('Error approving rollback', { migrationId, error: msg });
        }
    }, [accessToken, migrationId, navigate]);

    // ── Derived ────────────────────────────────────────────────────────────────

    const importedAt = migration?.job3Import?.completedAtUtc;
    const daysSince = computeDaysSince(importedAt);
    const groups = plan ? groupByType(plan.resources) : [];
    const planReady = pollStatus === 'ready' && plan !== null;

    const bannerConfig: BannerConfig | null =
        daysSince !== null ? buildBannerConfig(daysSince) : null;

    const completedLabel =
        daysSince === null ? '' :
            daysSince < 1 ? 'less than 24 hours ago' :
                `${daysSince} day${daysSince !== 1 ? 's' : ''} ago`;

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <>
            <div
                className="px-6 py-6 xl:py-5 lg:py-4 md:py-3 md:px-4 lg:px-5 transition-[padding] duration-300"
                style={{ maxWidth: '1536px', marginLeft: 'auto', marginRight: 'auto' }}
            >
                <div className="flex flex-col rounded-2xl border border-gray-300 bg-gray-200 shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden mb-2 p-1">
                    <div className={`flex flex-col flex-1 rounded-2xl border border-gray-200 bg-gray-100 shadow-xl dark:border-gray-600 dark:bg-gray-700 overflow-hidden m-2 ${ui.page.cardPadding}`}>

                        {/* ── Card 1: Migration info + warning ─────────────────── */}
                        <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.cardPadding} ${ui.mb.section}`}>

                            {/* Title */}
                            <div className={`flex items-start justify-between ${ui.mb.section}`}>
                                <div className="inline-flex flex-col items-start">
                                    <p className={`${ui.text.xs} font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1`}>
                                        Rollback Preview
                                    </p>
                                    <h2 className={`${ui.text.pageTitle} font-extrabold leading-none tracking-tight text-gray-800 dark:text-gray-100`}>
                                        {migration?.name ?? migrationId}
                                    </h2>
                                    <div aria-hidden="true" className="mt-2 h-1 w-16 bg-blue-600 dark:bg-blue-500 rounded" />
                                </div>

                            </div>

                            {/* Warning banner */}
                            <div
                                className={`flex items-start ${ui.gap.sm} ${ui.page.sectionPadding} rounded-lg border bg-yellow-50 border-yellow-300 dark:bg-yellow-900/30 dark:border-yellow-700 ${ui.mb.section}`}
                                role="alert"
                            >
                                <span className="text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0 text-xl" aria-hidden="true">⚠️</span>
                                <div className={`flex flex-col ${ui.gap.xs}`}>
                                    <p className={`font-semibold text-yellow-800 dark:text-yellow-200 ${ui.text.body}`}>
                                        You are about to initiate an automated rollback
                                    </p>
                                    <p className={`text-yellow-700 dark:text-yellow-300 ${ui.text.body}`}>
                                        This action will permanently modify your Genesys Cloud environment and cannot be automatically undone once started.
                                    </p>
                                </div>
                            </div>

                            {/* Metadata grid */}
                            <div className={`grid grid-cols-1 md:grid-cols-2 ${ui.gap.md} ${ui.mb.section}`}>
                                {/* Completion timestamp */}
                                <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
                                    <div className="flex-shrink-0 rounded-md bg-purple-100 dark:bg-purple-900/40 p-2">
                                        <svg className={`${ui.svg.sm} text-purple-600 dark:text-purple-400`} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>Migration Completed</p>
                                        <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100 truncate`}>
                                            {importedAt ? formatUtcTimestampLocal(importedAt) : '—'}
                                        </p>
                                    </div>
                                </div>

                                {/* Target org */}
                                <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
                                    <div className="flex-shrink-0 rounded-md bg-emerald-100 dark:bg-emerald-900/40 p-2">
                                        <svg className={`${ui.svg.sm} text-emerald-600 dark:text-emerald-400`} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>Target Organization</p>
                                        <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100 truncate`} title={migration?.targetOrgName ?? '—'}>
                                            {migration?.targetOrgName ?? '—'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Time-elapsed banner */}
                            {bannerConfig && (
                                <div className={`flex items-start ${ui.gap.sm} rounded-xl px-4 py-3 border ${ui.mb.title} ${bannerConfig.variant === 'green'
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                    : bannerConfig.variant === 'amber'
                                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                    }`}>
                                    <svg
                                        className={`flex-shrink-0 mt-0.5 ${ui.svg.sm} ${bannerConfig.variant === 'green'
                                            ? 'text-green-600 dark:text-green-400'
                                            : bannerConfig.variant === 'amber'
                                                ? 'text-amber-600 dark:text-amber-400'
                                                : 'text-red-600 dark:text-red-400'
                                            }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <p className={`${ui.text.body} font-semibold ${bannerConfig.variant === 'green'
                                        ? 'text-green-800 dark:text-green-200'
                                        : bannerConfig.variant === 'amber'
                                            ? 'text-amber-800 dark:text-amber-200'
                                            : 'text-red-800 dark:text-red-200'
                                        }`}>
                                        {bannerConfig.message}
                                    </p>
                                </div>
                            )}

                            {/* Static explainer */}
                            <div className="border-l-2 border-blue-300 dark:border-blue-700 pl-4 space-y-2">
                                <p className={`${ui.text.xs} font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1`}>
                                    About Automatic Rollback
                                </p>
                                <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                                    RepliCloud compares snapshots taken before and after this migration, then reverses only the differences — restoring your target org to the state it was in immediately before the migration ran.
                                </p>
                                <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                                    Resources created by this migration will be deleted. Resources modified will be reverted to their previous values. Resources deleted will be restored. Rollback is scoped strictly to this migration — changes made by other users after this migration ran are not affected.
                                </p>
                            </div>

                            {/* ── What happens when you confirm ───────────── */}
                            <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-700">
                                <p className={`${ui.text.xs} font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4`}>
                                    What happens when you click Confirm Rollback
                                </p>
                                <ol className="flex flex-col">
                                    {([
                                        {
                                            n: 1,
                                            title: 'Your review',
                                            body: 'Review every resource change in the plan below. Check each resource type and confirm you are happy before proceeding.',
                                            tag: 'You are here',
                                            color: 'blue' as const,
                                        },
                                        {
                                            n: 2,
                                            title: 'Automated rollback',
                                            body: 'Once you confirm, the rollback runs automatically in the background. No further action is needed.',
                                            tag: null,
                                            color: 'green' as const,
                                        },
                                    ] as const).map((step, idx, arr) => (
                                        <li key={step.n} className="flex gap-4">
                                            {/* Number circle + connector */}
                                            <div className="flex flex-col items-center shrink-0">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.color === 'green' ? 'bg-green-100 dark:bg-green-900/40' : 'bg-blue-100 dark:bg-blue-900/40'}`}>
                                                    <span className={`${ui.text.xs} font-bold ${step.color === 'green' ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}`}>
                                                        {step.n}
                                                    </span>
                                                </div>
                                                {idx < arr.length - 1 && (
                                                    <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 my-1" />
                                                )}
                                            </div>
                                            {/* Content */}
                                            <div className={`${idx < arr.length - 1 ? 'pb-4' : ''} min-w-0 flex-1`}>
                                                <div className={`flex items-center ${ui.gap.sm} mb-0.5`}>
                                                    <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100`}>
                                                        {step.title}
                                                    </p>
                                                    {step.tag && (
                                                        <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400`}>
                                                            {step.tag}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                                                    {step.body}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>

                        {/* ── Card 2: Rollback plan + actions ──────────────────── */}
                        <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.cardPadding}`}>

                            {/* Header row */}
                            <div className={`flex items-center justify-between flex-wrap ${ui.gap.md} ${ui.mb.title}`}>
                                <div className="flex items-center gap-2">
                                    {plan && (
                                        <span className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 px-2.5 py-0.5 rounded-full`}>
                                            {plan.totalResources}
                                        </span>
                                    )}
                                    <h3 className={`${ui.text.sectionTitle} font-bold text-gray-800 dark:text-gray-100`}>
                                        Rollback Plan
                                    </h3>
                                </div>

                                {/* Summary pills as legend */}
                                {planReady && (
                                    <div className={`flex items-center ${ui.gap.md}`}>
                                        <span className={`${ui.text.xs} flex items-center gap-1 font-semibold text-red-700 dark:text-red-400`}>
                                            <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                                            -{plan.resourcesToDelete} Deleted
                                        </span>
                                        <span className={`${ui.text.xs} flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-400`}>
                                            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                                            ~{plan.resourcesToRevert} Reverted
                                        </span>
                                        <span className={`${ui.text.xs} flex items-center gap-1 font-semibold text-green-600 dark:text-green-400`}>
                                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                                            +{plan.resourcesToRestore} Restored
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Loading */}
                            {pollStatus === 'loading' && (
                                <div className="flex items-center justify-center gap-3 py-10">
                                    <Spinner />
                                    <span className={`text-gray-500 dark:text-gray-400 ${ui.text.body}`}>
                                        Generating rollback preview…
                                    </span>
                                </div>
                            )}

                            {/* Error */}
                            {pollStatus === 'error' && (
                                <div className={`rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-700 ${ui.page.sectionPadding}`}>
                                    <p className={`text-red-700 dark:text-red-300 font-semibold ${ui.text.body} mb-1`}>
                                        Rollback preview failed
                                    </p>
                                    {errorMessage && (
                                        <p className={`text-red-600 dark:text-red-400 ${ui.text.xs} mb-3`}>{errorMessage}</p>
                                    )}
                                    <button
                                        onClick={handleRetry}
                                        className={`${ui.button.sm} rounded-md bg-red-600 text-white font-medium hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600`}
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}

                            {/* Ready — accordion */}
                            {planReady && (
                                <>
                                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                                        {groups.map((group) => {
                                            const total = group.deletes.length + group.reverts.length + group.restores.length;
                                            const allResources = [...group.deletes, ...group.reverts, ...group.restores];

                                            return (
                                                <details
                                                    key={group.resourceType}
                                                    className="group/type"
                                                >
                                                    <summary className={`flex items-center justify-between ${ui.page.rowPadding} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors list-none select-none`}>
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <ChevronRight className={`${ui.svg.sm} shrink-0 text-gray-400 dark:text-gray-500 transition-transform duration-200 group-open/type:rotate-90`} />
                                                            <span className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 px-2 py-0.5 rounded-full shrink-0`}>
                                                                {total}
                                                            </span>
                                                            <span className={`${ui.text.body} font-semibold text-gray-800 dark:text-gray-100 truncate`}>
                                                                {group.label}
                                                            </span>
                                                        </div>
                                                        <div className={`flex items-center gap-1.5 shrink-0 ml-3`}>
                                                            {group.deletes.length > 0 && (
                                                                <span className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}>
                                                                    -{group.deletes.length}
                                                                </span>
                                                            )}
                                                            {group.reverts.length > 0 && (
                                                                <span className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300`}>
                                                                    ~{group.reverts.length}
                                                                </span>
                                                            )}
                                                            {group.restores.length > 0 && (
                                                                <span className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300`}>
                                                                    +{group.restores.length}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </summary>

                                                    {/* Individual resource rows */}
                                                    <div className="bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800">
                                                        {allResources.map((resource) => {
                                                            const hasAttrs =
                                                                resource.action === 'revert' &&
                                                                (resource.changedAttributes?.length ?? 0) > 0;

                                                            const actionPillCls =
                                                                resource.action === 'delete'
                                                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                                    : resource.action === 'revert'
                                                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                                                        : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';

                                                            const actionLabel =
                                                                resource.action === 'delete'
                                                                    ? 'Will Be Deleted'
                                                                    : resource.action === 'revert'
                                                                        ? 'Will Be Reverted'
                                                                        : 'Will Be Restored';

                                                            if (hasAttrs) {
                                                                return (
                                                                    <details
                                                                        key={resource.resourceAddress}
                                                                        className="group/resource border-b border-gray-100 dark:border-gray-800/60 last:border-0"
                                                                    >
                                                                        <summary className={`flex items-center justify-between pl-8 pr-4 py-2.5 transition-colors list-none cursor-pointer select-none bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30`}>
                                                                            <div className="flex items-center gap-2.5 min-w-0">
                                                                                <ChevronRight className={`${ui.svg.sm} shrink-0 text-blue-400 dark:text-blue-500 transition-transform duration-200 group-open/resource:rotate-90`} />
                                                                                <span className={`${ui.text.label} font-medium text-blue-700 dark:text-blue-300 truncate`}>
                                                                                    {resource.resourceName}
                                                                                </span>
                                                                            </div>
                                                                            <span className={`${ui.text.xs} font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${actionPillCls}`}>
                                                                                {actionLabel}
                                                                            </span>
                                                                        </summary>

                                                                        {/* Property diff grid */}
                                                                        <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                                                                            <div className="pl-10 lg:pl-12 pr-4 py-3 lg:py-4">
                                                                                <div className="border-2 border-amber-200 dark:border-amber-800/60 rounded-lg overflow-hidden shadow-sm">
                                                                                    {/* Header */}
                                                                                    <div className="bg-amber-100 dark:bg-amber-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-amber-200 dark:border-amber-800/60 select-none">
                                                                                        <h4 className={`${ui.text.sm} font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider`}>
                                                                                            Property Changes ({resource.changedAttributes!.length})
                                                                                        </h4>
                                                                                    </div>
                                                                                    {/* Column headers */}
                                                                                    <div className="grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/40 select-none">
                                                                                        <span className={`${ui.text.xs} font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wide`}>Property</span>
                                                                                        <span className={`${ui.text.xs} font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wide`}>Current Value</span>
                                                                                        <span className={`${ui.text.xs} font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wide`}>Rollback Value</span>
                                                                                    </div>
                                                                                    {/* Rows */}
                                                                                    {resource.changedAttributes!.map((attr, idx) => (
                                                                                        <div
                                                                                            key={attr.attributeName}
                                                                                            className={`grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-amber-50/60 dark:hover:bg-amber-900/10 transition-colors cursor-default items-center ${idx % 2 === 0 ? 'bg-amber-50/40 dark:bg-amber-950/10' : 'bg-white dark:bg-gray-800'}`}
                                                                                        >
                                                                                            <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`}>
                                                                                                {formatPropertyName(attr.attributeName)}
                                                                                            </span>
                                                                                            <span className={`${ui.text.xs} lg:text-sm text-red-600 dark:text-red-400 font-mono truncate select-text`}>
                                                                                                {formatValue(attr.currentValue)}
                                                                                            </span>
                                                                                            <span className={`${ui.text.xs} lg:text-sm text-green-600 dark:text-green-400 font-mono truncate select-text`}>
                                                                                                {formatValue(attr.originalValue)}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </details>
                                                                );
                                                            }

                                                            // Non-expandable row (delete / restore)
                                                            return (
                                                                <div
                                                                    key={resource.resourceAddress}
                                                                    className={`flex items-center justify-between pl-8 pr-4 py-2.5 border-b border-gray-100 dark:border-gray-800/60 last:border-0 cursor-default transition-colors ${resource.action === 'delete' ? 'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30' : 'bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30'}`}
                                                                >
                                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                                        <span className="w-5 shrink-0" aria-hidden="true" />
                                                                        <span className={`${ui.text.label} font-medium truncate ${resource.action === 'delete' ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>
                                                                            {resource.resourceName}
                                                                        </span>
                                                                    </div>
                                                                    <span className={`${ui.text.xs} font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${actionPillCls}`}>
                                                                        {actionLabel}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </details>
                                            );
                                        })}
                                    </div>

                                    {/* Static scope note */}
                                    <p className={`text-gray-400 dark:text-gray-500 italic ${ui.text.xs} mt-3`}>
                                        This preview was generated from snapshots taken at the time of the original migration. Resources changed by other users after the migration completed are not shown and will not be affected by rollback.
                                    </p>
                                </>
                            )}

                            {/* Action buttons */}
                            {pollStatus !== 'error' && (
                                <div className={`flex items-center justify-between ${ui.gap.md} border-t border-gray-200 dark:border-gray-700 mt-6 pt-4`}>
                                    <button
                                        onClick={() => navigate('/dashboard')}
                                        className={`${ui.button.md} rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600`}
                                    >
                                        Cancel Rollback
                                    </button>
                                    <button
                                        disabled={!planReady}
                                        onClick={() => setConfirmOpen(true)}
                                        className={`${ui.button.md} rounded-md font-medium text-white transition-colors ${planReady
                                            ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
                                            : 'bg-blue-300 cursor-not-allowed dark:bg-blue-900/40 dark:text-blue-300'
                                            }`}
                                    >
                                        Confirm Rollback →
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </div>

            <RollbackConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                migrationName={migration?.name ?? migrationId}
                completedLabel={completedLabel}
                plan={plan}
                loading={approveLoading}
                onConfirm={handleConfirmRollback}
            />
        </>
    );
};

export default RollbackPreviewPage;
